import { useMemo, useRef, useEffect } from 'react';
import { List, ListListsResponse } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/v2/service.pb';
import { UseInfiniteQueryResult } from 'react-query';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { theme } from '@frontend/theme';
import { useAudienceTray } from '../providers/audience-tray-provider';
import { ListItem } from './list-item';
import { ListsEmptyState } from './lists-empty-state';
import { ListsErrorState } from './lists-error-state';
import { ListsFooterLoadingState } from './lists-footer-loader';
import { ListsLoadingState } from './lists-loading-state';
import { NewListItem } from './new-list-item';
import { RenameListItem } from './rename-list-item';

type Props = {
  startRenameList: (list: List) => void;
  cancelRenameList: () => void;
  finishRenameList: (updatedName: string) => Promise<void>;
  startDeleteList: (list: List) => void;
  startEditList: (list: List) => Promise<void>;
  cancelCreateList: () => void;
  finishCreateList: (listName: string) => Promise<void>;
  isListSelected: (list: List) => boolean;
  toggleList: (list: List) => void;
  isDebouncing: boolean;
  lists: List[];
  listsQueryResult: UseInfiniteQueryResult<ListListsResponse>;
};

export const ListsContent = ({
  startRenameList,
  cancelRenameList,
  finishRenameList,
  startDeleteList,
  startEditList,
  cancelCreateList,
  finishCreateList,
  isListSelected,
  toggleList,
  isDebouncing,
  lists: passedLists,
  listsQueryResult,
}: Props) => {
  const { listToUpdate, mode, campaignType } = useAudienceTray();
  const { isFetchingNextPage, isError, refetch, fetchNextPage } = listsQueryResult;
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const isCreatingList =
    mode === 'lists-create-from-lists' ||
    mode === 'lists-create-from-recipients' ||
    mode === 'lists-create-from-upload';

  const isRenaming = mode === 'lists-rename-list';

  useEffect(() => {
    if (isCreatingList && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex(0);
    }
  }, [isCreatingList]);

  const newListItemPlaceholder: List = useMemo(
    () => ({
      listId: 'new-list-item-placeholder',
      name: '',
      locationId: '',
      type: campaignType,
    }),
    [campaignType]
  );

  const lists = useMemo(
    () => (isCreatingList ? [newListItemPlaceholder, ...passedLists] : passedLists),
    [isCreatingList, newListItemPlaceholder, passedLists]
  );

  if (isError) return <ListsErrorState retry={refetch} />;
  if (isDebouncing) return <ListsLoadingState />;

  if (lists.length > 0) {
    return (
      <Virtuoso
        ref={virtuosoRef}
        style={{
          height: `calc(100% - ${theme.spacing(11)})`, // TODO: Figure out how to make this work without calc and when adding a new list item
          margin: theme.spacing(0, 4),
          paddingBottom: theme.spacing(2),
        }}
        data={lists}
        totalCount={lists.length}
        itemContent={(index, list) => {
          if (index === 0 && isCreatingList) {
            return (
              <NewListItem key={list.listId} cancelCreateList={cancelCreateList} finishCreateList={finishCreateList} />
            );
          }

          if (mode === 'lists-rename-list' && listToUpdate?.listId === list.listId) {
            return (
              <RenameListItem
                key={list.listId}
                list={list}
                cancelRenameList={cancelRenameList}
                finishRenameList={finishRenameList}
              />
            );
          }

          return (
            <ListItem
              key={list.listId}
              list={list}
              checked={isListSelected(list)}
              onCheck={() => toggleList(list)}
              startDeleteList={startDeleteList}
              startEditList={startEditList}
              startRenameList={startRenameList}
              disabled={isCreatingList || isRenaming}
            />
          );
        }}
        endReached={() => fetchNextPage({ cancelRefetch: false })}
        followOutput='smooth'
        components={{
          Footer: () => <ListsFooterLoadingState isFetchingNextPage={isFetchingNextPage} />,
        }}
      />
    );
  }

  return <ListsEmptyState />;
};
